// ============================================================================
// UNIFIED REPORT ENGINE — PUBLIC API
// ============================================================================
// Single import point: import { ... } from '@/lib/reports';
// ============================================================================

// Types
export type {
  DocumentCategory,
  DocumentType,
  LinkedAppointmentsDisplayMode,
  LinkedAppointmentsReferenceLabel,
  LinkedAppointmentsTimeDisplayMode,
  UnifiedSectionType,
  SectionTypeMeta,
  UnifiedTemplateSection,
  UnifiedTemplateStyles,
  UnifiedTemplateLayout,
  TemplateTableColumn,
  TemplateEmailSettings,
  UnifiedTemplate,
  MergeTag,
} from './types';

// Input types (Phase 2)
export type {
  UnifiedReportInput,
  ChildCardsMetaInput,
  ChildCardInput,
  ChildCardSummarySectionInput,
  SummaryCardInput,
  FieldSectionInput,
  LineItemInput,
  TotalsInput,
  BankDetailsInput,
  DataTableInput,
  ChecklistInput,
  TimeEntryInput,
  MaterialInput,
  PhotoInput,
  GPSInput,
  SignatureInput,
  CertificateInput,
  TermsNotesInput,
  PaymentInfoInput,
} from './UnifiedReportInput';

export type { AttendanceCardGpaDisplayMode } from './operationsReportFilters';

// Constants & lookups
export {
  DOCUMENT_TYPE_CATEGORY,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPE_ICONS,
  DOCUMENT_CATEGORY_META,
  SUPPORTED_UNIFIED_REPORT_DOCUMENT_TYPES,
  TIME_ATTENDANCE_REPORT_DOCUMENT_TYPES,
  WORKFORCE_REPORT_DOCUMENT_TYPES,
  REPORT_LIBRARY_DOCUMENT_TYPES,
  SECTION_TYPE_META,
  DEFAULT_UNIFIED_STYLES,
  DEFAULT_UNIFIED_LAYOUT,
  DEFAULT_LINE_ITEM_COLUMNS,
  DELIVERY_NOTE_COLUMNS,
  getDefaultTemplateName,
  getDocumentTypesByCategory,
  getSectionTypesForCategory,
  getSectionTypesForDocumentType,
  isUnifiedReportDocumentType,
} from './types';

// Section registry
export {
  getDefaultSections,
  getDefaultTableColumns,
  getDefaultComprehensiveOperationsSections,
  getOperationsSectionSourceOptions,
  normalizeTemplateSections,
} from './sectionRegistry';

// Merge tag registry
export { getMergeTags, getMergeTagsByCategory, replaceMergeTags } from './mergeTagRegistry';

// Operations report helpers
export {
  normalizeAttendanceCardGpaDisplayMode,
  resolveAttendanceCardGpaDisplayMode,
  shouldIncludeAppointmentInOperationsReport,
} from './operationsReportFilters';

export {
  hasMeaningfulReportValue,
  filterGroupedAttendanceCardFieldSections,
  filterReportFieldSectionFields,
  filterReportTimeEntries,
} from './operationsReportVisibility';

// ── Phase 2: Unified Generators ─────────────────────────────────────────────
// NOTE: Generators are NOT re-exported here because they import server-only
// modules (fs / path / Puppeteer runtime deps) that break client component bundles.
// Turbopack resolves the full module graph even for unused barrel exports.
//
// Server-side callers (API routes) should import directly:
//   import { generateUnifiedPDF, generateUnifiedPDFBuffer, generateUnifiedPDFBase64, generateUnifiedPDFDataUri } from '@/lib/reports/generateUnifiedPDF';
//   import { generateUnifiedHTML } from '@/lib/reports/generateUnifiedHTML';

// ── Phase 3: Data Adapters ──────────────────────────────────────────────────
// NOTE: Adapters are NOT re-exported here because they import server-only
// modules (database.ts → knex → prom-client → fs/v8/cluster).
// Even `export type { ... } from './adapters'` causes Turbopack to resolve
// the module graph and bundle server code into the client.
//
// Server-side callers (API routes) should import directly:
//   import { buildReportInput, getSupportedDocumentTypes } from '@/lib/reports/adapters';
//   import type { BuildReportParams, AdapterBranding } from '@/lib/reports/adapters';

// ── Phase 5: Unified Report Email ───────────────────────────────────────────
// NOTE: sendReportEmail is NOT re-exported here either — same reason.
// Even `export type` re-exports cause Turbopack to resolve the full module.
//
// Server-side callers should import directly:
//   import { sendReportEmail } from '@/lib/reports/sendReportEmail';
